import { useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Key, Loader2, RotateCcw, X, Ban, CheckCircle2 } from 'lucide-react';
import { api } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { SecretDisplayDialog } from './SecretDisplayDialog';

export interface OAuthClientItem {
    id: string;
    client_id: string;
    name: string;
    description: string;
    status: string;
    scopes: string[];
    environment: string;
    last_used_at?: string;
    created: string;
    modified: string;
}

interface OAuthClientDetailDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    onUpdated: () => void;
    client: OAuthClientItem | null;
    apiPath: string;
}

export function OAuthClientDetailDrawer({
    isOpen,
    onClose,
    onUpdated,
    client,
    apiPath,
}: OAuthClientDetailDrawerProps) {
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [confirmAction, setConfirmAction] = useState<'revoke' | 'regenerate' | null>(null);
    const [regeneratedCreds, setRegeneratedCreds] = useState<{ client_id: string; client_secret: string } | null>(null);

    if (!client) return null;

    const isActive = client.status === 'Active';
    const isDisabled = client.status === 'Disabled';
    const isRevoked = client.status === 'Revoked';

    const handleDisable = async () => {
        setActionLoading('disable');
        try {
            await api.post(`${apiPath}/${client.id}/disable`);
            onUpdated();
        } finally {
            setActionLoading(null);
        }
    };

    const handleEnable = async () => {
        setActionLoading('enable');
        try {
            await api.post(`${apiPath}/${client.id}/enable`);
            onUpdated();
        } finally {
            setActionLoading(null);
        }
    };

    const handleRevoke = async () => {
        setActionLoading('revoke');
        try {
            await api.delete(`${apiPath}/${client.id}`);
            setConfirmAction(null);
            onUpdated();
        } finally {
            setActionLoading(null);
        }
    };

    const handleRegenerate = async () => {
        setActionLoading('regenerate');
        try {
            const { data } = await api.post<{ client_id: string; client_secret: string }>(
                `${apiPath}/${client.id}/regenerate`,
            );
            setRegeneratedCreds(data);
            setConfirmAction(null);
            onUpdated();
        } finally {
            setActionLoading(null);
        }
    };

    const formatDate = (iso: string) => {
        try {
            return new Date(iso).toLocaleDateString(undefined, {
                dateStyle: 'medium',
            });
        } catch {
            return iso;
        }
    };

    const statusBadge = (status: string) => {
        const map: Record<string, string> = {
            Active: 'bg-green-100 text-green-700',
            Disabled: 'bg-yellow-100 text-yellow-700',
            Revoked: 'bg-red-100 text-red-700',
        };
        return (
            <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${map[status] ?? 'bg-slate-100 text-slate-700'}`}>
                {status}
            </span>
        );
    };

    return (
        <>
            {regeneratedCreds && (
                <SecretDisplayDialog
                    open={!!regeneratedCreds}
                    onClose={() => setRegeneratedCreds(null)}
                    clientId={regeneratedCreds.client_id}
                    clientSecret={regeneratedCreds.client_secret}
                    name={client.name}
                />
            )}

            {createPortal(
                <AnimatePresence>
                    {isOpen && (
                        <div className="fixed inset-0 z-[200]" style={{ pointerEvents: 'auto' }}>
                            <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                className="fixed inset-0 z-[200] bg-black/50 backdrop-blur-sm"
                                onClick={onClose}
                            />

                            <motion.div
                                initial={{ x: '100%' }}
                                animate={{ x: 0 }}
                                exit={{ x: '100%' }}
                                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                                className="fixed bottom-0 right-0 top-0 z-[201] flex w-[450px] flex-col overflow-hidden border-l border-slate-200 bg-white shadow-2xl"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="relative border-b border-slate-100 bg-slate-50/50 px-6 py-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-100">
                                                <Key className="h-4 w-4 text-indigo-600" />
                                            </div>
                                            <div>
                                                <h2 className="text-lg font-semibold text-slate-900">{client.name}</h2>
                                                <p className="text-xs text-slate-500">{client.client_id}</p>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-600"
                                        >
                                            <X size={18} />
                                        </button>
                                    </div>
                                </div>

                                <div className="flex-1 overflow-y-auto p-6">
                                    <div className="space-y-5">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <p className="text-xs font-medium text-slate-500">Status</p>
                                                <div className="mt-1">{statusBadge(client.status)}</div>
                                            </div>
                                            <div>
                                                <p className="text-xs font-medium text-slate-500">Environment</p>
                                                <p className="mt-1 text-sm text-slate-900 capitalize">{client.environment}</p>
                                            </div>
                                        </div>

                                        {client.description && (
                                            <div>
                                                <p className="text-xs font-medium text-slate-500">Description</p>
                                                <p className="mt-1 text-sm text-slate-700">{client.description}</p>
                                            </div>
                                        )}

                                        <div>
                                            <p className="text-xs font-medium text-slate-500">Scopes</p>
                                            <div className="mt-1 flex flex-wrap gap-1.5">
                                                {client.scopes.length > 0 ? (
                                                    client.scopes.map((s) => (
                                                        <code
                                                            key={s}
                                                            className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700"
                                                        >
                                                            {s}
                                                        </code>
                                                    ))
                                                ) : (
                                                    <span className="text-xs text-slate-400">No scopes</span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <p className="text-xs font-medium text-slate-500">Created</p>
                                                <p className="mt-1 text-sm text-slate-700">{formatDate(client.created)}</p>
                                            </div>
                                            <div>
                                                <p className="text-xs font-medium text-slate-500">Last Used</p>
                                                <p className="mt-1 text-sm text-slate-700">
                                                    {client.last_used_at ? formatDate(client.last_used_at) : 'Never'}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {!isRevoked && (
                                    <div className="space-y-3 border-t border-slate-100 bg-slate-50/50 px-6 py-4">
                                        <div className="flex gap-2">
                                            {isActive && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    disabled={!!actionLoading}
                                                    onClick={() => void handleDisable()}
                                                    className="flex items-center gap-1.5"
                                                >
                                                    {actionLoading === 'disable' ? (
                                                        <Loader2 size={14} className="animate-spin" />
                                                    ) : (
                                                        <Ban size={14} />
                                                    )}
                                                    Disable
                                                </Button>
                                            )}
                                            {isDisabled && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    disabled={!!actionLoading}
                                                    onClick={() => void handleEnable()}
                                                    className="flex items-center gap-1.5"
                                                >
                                                    {actionLoading === 'enable' ? (
                                                        <Loader2 size={14} className="animate-spin" />
                                                    ) : (
                                                        <CheckCircle2 size={14} />
                                                    )}
                                                    Enable
                                                </Button>
                                            )}
                                            <Button
                                                variant="outline"
                                                size="sm"
                                                disabled={!!actionLoading}
                                                onClick={() => setConfirmAction('regenerate')}
                                                className="flex items-center gap-1.5"
                                            >
                                                <RotateCcw size={14} />
                                                Regenerate
                                            </Button>
                                        </div>
                                        <Button
                                            variant="destructive"
                                            size="sm"
                                            disabled={!!actionLoading}
                                            onClick={() => setConfirmAction('revoke')}
                                            className="w-full"
                                        >
                                            Revoke Client
                                        </Button>
                                    </div>
                                )}
                            </motion.div>
                        </div>
                    )}
                </AnimatePresence>,
                document.body,
            )}

            <AlertDialog open={confirmAction === 'revoke'} onOpenChange={(o) => !o && setConfirmAction(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Revoke OAuth Client</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently invalidate the OAuth client. Any integrations using it will immediately stop
                            working. This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={!!actionLoading}>Cancel</AlertDialogCancel>
                        <Button variant="destructive" disabled={!!actionLoading} onClick={() => void handleRevoke()}>
                            {actionLoading === 'revoke' ? 'Revoking...' : 'Revoke'}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={confirmAction === 'regenerate'} onOpenChange={(o) => !o && setConfirmAction(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Regenerate Credentials</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will generate a new client ID and secret. The old credentials will immediately stop working.
                            Make sure to update all integrations with the new credentials.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={!!actionLoading}>Cancel</AlertDialogCancel>
                        <Button disabled={!!actionLoading} onClick={() => void handleRegenerate()}>
                            {actionLoading === 'regenerate' ? 'Regenerating...' : 'Regenerate'}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
